import React, { useCallback, useEffect, useRef, useState } from "react";
import { ModalShell, Button } from "../../../components/ui";
import API_UTIL from "../../../services/AuthIntereptor";
import { toast } from "react-toastify";

/**
 * Web biometric-consent signing gate.
 *
 * Embeds DocuSeal using their official <docuseal-form> web component (loaded from their
 * CDN). A raw iframe of the hosted /s/{slug} page is blocked by X-Frame-Options, so the
 * official component — which frames a dedicated embeddable endpoint — is required.
 * On the component's "completed" event we verify server-side via /api/web-consent/confirm
 * before letting the user proceed. Additive — does not touch the mobile consent flow.
 */

const DOCUSEAL_SCRIPT_SRC = "https://cdn.docuseal.com/js/form.js";

function loadDocusealScript() {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${DOCUSEAL_SCRIPT_SRC}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = DOCUSEAL_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load DocuSeal embed script"));
    document.body.appendChild(script);
  });
}

function DocuSealConsentModal({ open, phone, name, onSigned }) {
  const [loading, setLoading] = useState(true);
  const [signingUrl, setSigningUrl] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [scriptReady, setScriptReady] = useState(false);
  const formRef = useRef(null);

  const startSubmission = useCallback(async () => {
    if (!phone) return;
    try {
      setLoading(true);
      setError(null);
      const res = await API_UTIL.post("/api/web-consent/create-submission", { phone, name });
      if (res.data && res.data.success && res.data.signingUrl) {
        setSigningUrl(res.data.signingUrl);
      } else {
        setError("Could not start the agreement. Please try again.");
      }
    } catch (e) {
      console.error("web-consent create-submission failed:", e);
      setError("Could not start the agreement. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [phone, name]);

  // Create the submission when the modal opens.
  useEffect(() => {
    if (open && phone && !signingUrl) {
      startSubmission();
    }
  }, [open, phone, signingUrl, startSubmission]);

  // Load the DocuSeal embed script when the modal opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    loadDocusealScript()
      .then(() => {
        if (!cancelled) setScriptReady(true);
      })
      .catch((e) => {
        console.error(e);
        if (!cancelled) setError("Could not load the signing form. Please try again.");
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleConfirm = useCallback(async () => {
    if (!phone) return;
    setConfirming(true);
    try {
      const res = await API_UTIL.post("/api/web-consent/confirm", { phone });
      if (res.data && res.data.success && res.data.completed) {
        toast.success("Agreement signed. Thank you!");
        onSigned();
      } else {
        toast.info("Please complete and submit the signature in the document first.");
      }
    } catch (e) {
      console.error("web-consent confirm failed:", e);
      toast.error("Could not verify signing. Please try again.");
    } finally {
      setConfirming(false);
    }
  }, [phone, onSigned]);

  // Auto-confirm when the DocuSeal component reports completion.
  useEffect(() => {
    const el = formRef.current;
    if (!el) return;
    const onCompleted = () => handleConfirm();
    el.addEventListener("completed", onCompleted);
    return () => el.removeEventListener("completed", onCompleted);
  }, [handleConfirm, signingUrl, scriptReady]);

  const formReady = !loading && !error && signingUrl && scriptReady;

  return (
    <ModalShell open={open} onClose={() => {}} maxWidth="max-w-3xl">
      <div className="p-6">
        <h2 className="text-lg font-bold text-slate-900">Biometric Consent Agreement</h2>
        <p className="text-sm text-slate-500 mt-1 mb-4">
          Please review and sign the agreement below to continue to your account.
        </p>

        {(loading || (!error && signingUrl && !scriptReady)) && (
          <div className="py-16 text-center text-slate-500">Preparing your agreement…</div>
        )}

        {!loading && error && (
          <div className="py-10 text-center">
            <p className="text-sm text-red-600 mb-4">{error}</p>
            <Button
              variant="secondary"
              onClick={() => {
                setError(null);
                if (!signingUrl) startSubmission();
              }}
            >
              Retry
            </Button>
          </div>
        )}

        {formReady && (
          <>
            <div
              className="rounded-xl overflow-hidden border border-gray-200"
              style={{ maxHeight: "60vh", overflowY: "auto" }}
            >
              {React.createElement("docuseal-form", {
                ref: formRef,
                "data-src": signingUrl,
                ...(name ? { "data-name": name } : {}),
              })}
            </div>
            <div className="mt-4">
              <Button className="w-full" onClick={handleConfirm} disabled={confirming}>
                {confirming ? "Verifying…" : "I have signed — Continue"}
              </Button>
              <p className="text-center text-[11px] text-slate-500 mt-2">
                Complete and submit the signature in the document above, then click Continue.
              </p>
            </div>
          </>
        )}
      </div>
    </ModalShell>
  );
}

export default DocuSealConsentModal;
